import { FormattedMessage } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { openedStructureSetApprovalConfirmationDialogState } from '~/state/atoms/structureSet/roiAtoms';
import {
    currentStructureSetState,
    structureSetReadModelFamily,
} from '~/state/atoms/structureSet/structureSetAtoms';

const StructureSetApprovalStatusConfirmationDialog = ({
    onApprovalStatusChange,
}: {
    onApprovalStatusChange: (
        status: DICOMApprovalStatus,
        reviewerName?: string,
        targetStructureSetUid?: string
    ) => void;
}) => {
    const [
        openedStructureSetApprovalConfirmationDialog,
        setOpenedStructureSetApprovalConfirmationDialog,
    ] = useRecoilState(openedStructureSetApprovalConfirmationDialogState);
    // The set the decision was taken about, not necessarily the one selected now.
    const namedStructureSet = useRecoilValue(
        structureSetReadModelFamily(
            openedStructureSetApprovalConfirmationDialog?.sopUid ?? ''
        )
    );
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const targetStructureSet =
        namedStructureSet ??
        (currentStructureSet?.dicomUid ===
        openedStructureSetApprovalConfirmationDialog?.sopUid
            ? currentStructureSet
            : undefined);

    const handleStructureSetApprovalStatusChangeConfirmation = () => {
        if (openedStructureSetApprovalConfirmationDialog) {
            onApprovalStatusChange(
                openedStructureSetApprovalConfirmationDialog.newApprovalStatus,
                undefined,
                openedStructureSetApprovalConfirmationDialog.sopUid
            );
            setOpenedStructureSetApprovalConfirmationDialog(null);
        }
    };

    const handleStructureSetApprovalStatusChangeDialogCancel = () => {
        setOpenedStructureSetApprovalConfirmationDialog(null);
    };

    const isApprovingStructureSet =
        openedStructureSetApprovalConfirmationDialog?.newApprovalStatus ===
        DICOMApprovalStatus.APPROVED;

    const rejectedRois = isApprovingStructureSet
        ? (targetStructureSet?.roiArray.filter(
              (roi) => roi.approvalStatus === DICOMApprovalStatus.REJECTED
          ) ?? [])
        : [];

    const hasRejectedRois = rejectedRois.length > 0;

    const baseMessage = (
        <FormattedMessage
            id="structureSetApprovalStatusChangeDescription"
            values={{
                newApprovalStatus: (
                    <strong>
                        {
                            openedStructureSetApprovalConfirmationDialog?.newApprovalStatus
                        }
                    </strong>
                ),
            }}
        />
    );

    const message = hasRejectedRois ? (
        <>
            {baseMessage}
            <br />
            <br />
            <FormattedMessage
                id="structureSetApproveOverridesRejectedWarning"
                values={{
                    rejectedNames: (
                        <strong>
                            {rejectedRois.map((r) => r.name).join(', ')}
                        </strong>
                    ),
                }}
            />
        </>
    ) : (
        baseMessage
    );

    return (
        openedStructureSetApprovalConfirmationDialog && (
            <ConfirmationDialog
                open={!!targetStructureSet}
                onCancel={handleStructureSetApprovalStatusChangeDialogCancel}
                onConfirm={handleStructureSetApprovalStatusChangeConfirmation}
                title={<FormattedMessage id="confirmApprovalStatusChange" />}
                message={message}
                cancelText={
                    hasRejectedRois ? (
                        <FormattedMessage id="goBack" />
                    ) : (
                        <FormattedMessage id="cancel" />
                    )
                }
                confirmText={<FormattedMessage id="confirm" />}
                mode={hasRejectedRois ? 'warning' : 'secondary'}
            />
        )
    );
};

export default StructureSetApprovalStatusConfirmationDialog;
